import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const APP_ROLE_SEARCH_RESOURCE = "app-role" as const;
export const APP_ROLE_SEARCH_OPERATION = "search" as const;
export const APP_ROLE_SEARCH_API_PATH = `/v1/${APP_ROLE_SEARCH_RESOURCE}/${APP_ROLE_SEARCH_OPERATION}` as const;

export type AppRoleSearchParameters = paths[typeof APP_ROLE_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type AppRoleSearchBody =
  paths[typeof APP_ROLE_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type AppRoleSearchResponse =
  paths[typeof APP_ROLE_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useAppRoleSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<AppRoleSearchParameters, AppRoleSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<AppRoleSearchResponse, Error, AppRoleSearchResponse, readonly unknown[]>;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<AppRoleSearchParameters, AppRoleSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      APP_ROLE_SEARCH_RESOURCE,
      APP_ROLE_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<AppRoleSearchResponse>(
        APP_ROLE_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
